import { useMemo } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { AlertCard, OwnerJournal } from "./Home";
import { buildSystemAlerts } from "../lib/alerts";
import { openAlertTarget } from "../lib/setupRoute";
import { buildWhatsAppLink, interpolateTemplate } from "../lib/marketing";
import { useWhatsAppStatus } from "../hooks/useWhatsAppStatus";

/**
 * Avisos — central feed dos alertas inteligentes do sistema + Diário do Dono.
 * - Alertas (estoque baixo, aniversariantes, inativos, faturamento, atrasos) também
 *   continuam aparecendo na Home, mas aqui ganham uma visão dedicada e expandida.
 * - O Diário do Dono foi MOVIDO da Home para este módulo.
 */
const Avisos = () => {
    const {
        brand,
        clients,
        products,
        professionals,
        appointments,
        templates,
        setActiveModule,
        logMessage,
    } = useSalon();

    const { status: waStatus } = useWhatsAppStatus(10000);
    const alerts = useMemo(
        () => buildSystemAlerts({ products, clients, appointments, whatsapp: waStatus }),
        [products, clients, appointments, waStatus]
    );

    // Group alerts by kind
    const counts = useMemo(() => {
        const out = { critical: 0, warning: 0, success: 0, info: 0 };
        alerts.forEach((a) => { out[a.kind] = (out[a.kind] || 0) + 1; });
        return out;
    }, [alerts]);

    const handleAlertClick = (alert) => {
        openAlertTarget(alert, setActiveModule);
    };

    const handleWhatsApp = (alert) => {
        const tpl = templates.find((t) => t.id === alert.whatsappTemplateId);
        if (!tpl || !alert.whatsappClient) return;
        const msg = interpolateTemplate(tpl, alert.whatsappClient, alert.whatsappContext || {}, brand);
        const link = buildWhatsAppLink(alert.whatsappClient.phone, msg);
        window.open(link, "_blank", "noopener");
        logMessage({
            clientId: alert.whatsappClient.id,
            channel: "whatsapp",
            campaignId: alert.id === "birthdays" ? "cmp-aniversariantes" : "cmp-reativacao-corte",
            status: "delivered",
        });
        toast.success(`WhatsApp aberto · ${alert.whatsappClient.name.split(" ")[0]}`, {
            description: "Mensagem registrada no histórico de campanhas.",
        });
    };

    return (
        <div data-testid="avisos-module" className="space-y-6">
            <header
                data-testid="avisos-header"
                className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/8 via-black/60 to-black/80 p-6 lg:p-7"
            >
                <div
                    className="absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl opacity-40 pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${brand.primaryColor || "#d4af37"}55 0%, transparent 70%)` }}
                ></div>
                <div className="relative flex items-start gap-3">
                    <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-300 flex items-center justify-center">
                        <i className="fa-solid fa-bell"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold">
                            Central de Avisos
                        </div>
                        <h1 className="salon-heading text-2xl lg:text-3xl text-amber-100 mt-1 leading-tight">
                            Tudo o que precisa da sua atenção
                        </h1>
                        <p className="text-xs text-slate-400 mt-2 max-w-2xl">
                            Alertas inteligentes do sistema, marcos do seu salão e oportunidades de ação. O que aparece aqui também é refletido na Home.
                        </p>
                    </div>
                </div>

                {/* KPI strip */}
                <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <CountChip color="red" icon="fa-triangle-exclamation" label="Críticos" value={counts.critical} testId="avisos-count-critical" />
                    <CountChip color="amber" icon="fa-circle-exclamation" label="Atenção" value={counts.warning} testId="avisos-count-warning" />
                    <CountChip color="emerald" icon="fa-circle-check" label="Bons sinais" value={counts.success} testId="avisos-count-success" />
                    <CountChip color="sky" icon="fa-circle-info" label="Oportunidades" value={counts.info} testId="avisos-count-info" />
                </div>
            </header>

            {/* Alerts feed */}
            <section data-testid="avisos-alerts-feed" className="rounded-2xl border border-amber-500/15 bg-slate-900/40 backdrop-blur-xl p-5 lg:p-6">
                <header className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-amber-500/12 border border-amber-500/30 text-amber-300 flex items-center justify-center">
                            <i className="fa-solid fa-bell-concierge"></i>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                                Avisos do Sistema
                            </div>
                            <h2 className="salon-heading text-lg lg:text-xl text-white mt-0.5">
                                Mural de Alertas
                            </h2>
                        </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                        {alerts.length} aviso{alerts.length === 1 ? "" : "s"}
                    </span>
                </header>

                {alerts.length === 0 ? (
                    <div className="text-center py-10 text-slate-500">
                        <i className="fa-solid fa-mug-hot text-2xl block mb-2 text-emerald-400/70"></i>
                        <p className="text-sm">Nenhum alerta pendente. Salão sob controle.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="avisos-alerts-grid">
                        {alerts.map((a) => (
                            <AlertCard
                                key={a.id}
                                alert={a}
                                onClick={() => handleAlertClick(a)}
                                onWhatsApp={() => handleWhatsApp(a)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Diário do Dono (moved from Home) */}
            <OwnerJournal
                brand={brand}
                clients={clients}
                professionals={professionals}
                appointments={appointments}
            />
        </div>
    );
};

const CHIP_COLORS = {
    red: { bg: "bg-red-500/10 border-red-500/30 text-red-300", icon: "text-red-300" },
    amber: { bg: "bg-amber-500/10 border-amber-500/30 text-amber-200", icon: "text-amber-300" },
    emerald: { bg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200", icon: "text-emerald-300" },
    sky: { bg: "bg-sky-500/10 border-sky-500/30 text-sky-200", icon: "text-sky-300" },
};

const CountChip = ({ color, icon, label, value, testId }) => {
    const c = CHIP_COLORS[color] || CHIP_COLORS.amber;
    return (
        <div data-testid={testId} className={`rounded-xl border ${c.bg} px-3 py-2 flex items-center gap-2.5`}>
            <i className={`fa-solid ${icon} ${c.icon}`}></i>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider opacity-80 truncate">{label}</div>
                <div className="salon-heading text-lg leading-none mt-0.5">{value}</div>
            </div>
        </div>
    );
};

export default Avisos;
